import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Select, Input, Button, message, InputNumber, Switch, AutoComplete, Modal } from 'antd';
import { ShoppingOutlined, SearchOutlined, DollarOutlined, CheckCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import { $api } from 'services/service';
import { ITicketType, formatCurrency } from '../ticketModels';
import browserPrinterService from 'services/browserPrinter.service';
import { usePrintSettings } from 'contexts/PrintSettingsContext';

const { Option } = Select;
const { TextArea } = Input;

interface TicketSaleProps {
    initialStudent?: any;
    autoUseOnLoad?: boolean;
    onStudentUsed?: () => void;
}

const TicketSale: React.FC<TicketSaleProps> = ({ initialStudent, autoUseOnLoad, onStudentUsed }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [ticketTypes, setTicketTypes] = useState<ITicketType[]>([]);
    const [selectedTicketType, setSelectedTicketType] = useState<ITicketType | null>(null);
    const [searchingStudent, setSearchingStudent] = useState(false);
    const [showNote, setShowNote] = useState(false);
    const [currentAmount, setCurrentAmount] = useState(1);
    const [studentSuggestions, setStudentSuggestions] = useState<any[]>([]);
    const [studentSearchText, setStudentSearchText] = useState('');
    const [selectedStudent, setSelectedStudent] = useState<any>(null);
    const { printSettings } = usePrintSettings();
    const debounceTimer = useRef<any>(null);

    useEffect(() => {
        loadTicketTypes();
        form.setFieldsValue({ amount: 1, isUsed: false });
    }, []);

    // Xử lý khi có initialStudent được truyền vào
    useEffect(() => {
        if (initialStudent) {
            setSelectedStudent(initialStudent);
            form.setFieldsValue({ studentId: initialStudent.id });
            setStudentSearchText('');
            setStudentSuggestions([]);

            // Tự động bật "Tự động dùng" nếu autoUseOnLoad = true
            if (autoUseOnLoad) {
                form.setFieldsValue({ isUsed: true });
            }

            // Gọi callback để thông báo đã sử dụng initialStudent
            if (onStudentUsed) {
                onStudentUsed();
            }
        }
    }, [initialStudent, autoUseOnLoad, form, onStudentUsed]);

    const loadTicketTypes = async () => {
        try {
            const res = await $api.ticketType.getAll();
            if (res.result) {
                const activeTypes = res.result.filter((t: ITicketType) => t.isActive);
                setTicketTypes(activeTypes);
            }
        } catch (error) {
            message.error('Không thể tải danh sách loại phiếu');
        }
    };

    const searchStudents = async (keyword: string) => {
        if (!keyword || keyword.length < 2) {
            setStudentSuggestions([]);
            return;
        }

        setSearchingStudent(true);
        try {
            const res = await $api.student.getPaging({
                pageIndex: 1,
                pageSize: 20,
                freeTextSearch: keyword
            });
            if (res.result?.items) {
                const options = res.result.items.map((student: any) => ({
                    value: student.id,
                    label: (
                        <div className="py-2">
                            <div className="font-semibold text-blue-600">
                                {student.fullName || student.name}
                            </div>
                            <div className="text-sm text-gray-600">
                                MSSV: {student.userInternalId || student.studentCode}
                            </div>
                        </div>
                    ),
                    student: student
                }));
                setStudentSuggestions(options);
            }
        } catch (error) {
            console.error('Error searching students:', error);
        } finally {
            setSearchingStudent(false);
        }
    };

    const handleStudentInputChange = (value: string) => {
        setStudentSearchText(value);
        
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            searchStudents(value);
        }, 500);
    };

    const handleStudentSelect = (value: string, option: any) => {
        form.setFieldsValue({ studentId: value });
        setSelectedStudent(option.student);
        setStudentSearchText('');
        setStudentSuggestions([]);
    };

    const handleStudentKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (studentSuggestions.length > 0) {
                const firstStudent = studentSuggestions[0];
                handleStudentSelect(firstStudent.value, firstStudent);
            }
        }
    };

    const handleTicketTypeChange = (ticketTypeId: string) => {
        const selected = ticketTypes.find(t => t.id === ticketTypeId);
        setSelectedTicketType(selected || null);
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            const response = await $api.ticket.create({
                studentId: values.studentId,
                ticketTypeId: values.ticketTypeId,
                note: values.note,
                amount: values.amount || 1,
                isUsed: values.isUsed || false,
                isPrinted: printSettings.AllowPrintTicketWhenSell ? true : null
            });

            // In vé tự động nếu AllowPrintTicketWhenSell = 1
            let printSuccess = false;
            if (printSettings.AllowPrintTicketWhenSell && response?.result && Array.isArray(response.result)) {
                try {
                    // In tất cả vé cùng lúc bằng trình duyệt
                    message.loading({ content: 'Đang mở cửa sổ in...', key: 'printing' });

                    // Chuẩn bị dữ liệu tất cả các vé
                    const ticketsData = response.result.map((ticket: any) => ({
                        id: ticket.id,
                        code: ticket.code,
                        studentName: ticket.studentName,
                        studentUserInternalId: ticket.studentUserInternalId,
                        ticketTypeName: ticket.ticketTypeName,
                        ticketTypeCode: ticket.ticketTypeCode,
                        price: ticket.price,
                        createdByName: ticket.createdByName,
                        creationDate: ticket.creationDate,
                        expireDate: ticket.expireDate,
                        qrCode: ticket.qrCode
                    }));

                    // In nhiều phiếu cùng lúc
                    browserPrinterService.printMultipleTickets(ticketsData);

                    message.success({ content: 'Đã mở cửa sổ in!', key: 'printing', duration: 2 });
                    printSuccess = true;
                } catch (printError: any) {
                    console.error('Print error:', printError);
                    message.error({
                        content: `Không thể in vé: ${printError.message || 'Lỗi không xác định'}. Bạn có thể in lại bằng nút "In lại".`,
                        duration: 5
                    });
                }
            }

            // Hàm in lại vé
            const handleReprintTickets = async () => {
                try {
                    message.loading({ content: 'Đang mở cửa sổ in...', key: 'reprinting' });

                    // Chuẩn bị dữ liệu tất cả các vé
                    const ticketsData = response.result.map((ticket: any) => ({
                        id: ticket.id,
                        code: ticket.code,
                        studentName: ticket.studentName,
                        studentUserInternalId: ticket.studentUserInternalId,
                        ticketTypeName: ticket.ticketTypeName,
                        ticketTypeCode: ticket.ticketTypeCode,
                        price: ticket.price,
                        createdByName: ticket.createdByName,
                        creationDate: ticket.creationDate,
                        expireDate: ticket.expireDate,
                        qrCode: ticket.qrCode
                    }));

                    // In nhiều phiếu cùng lúc
                    browserPrinterService.printMultipleTickets(ticketsData);

                    message.success({ content: 'Đã mở cửa sổ in!', key: 'reprinting', duration: 2 });
                } catch (error: any) {
                    message.error({
                        content: `Không thể in vé: ${error.message || 'Lỗi không xác định'}`,
                        key: 'reprinting',
                        duration: 5
                    });
                }
            };

            // Hiển thị Modal thông báo thành công với thông tin chi tiết
            Modal.success({
                icon: null,
                title: (
                    <div className="text-center">
                        <CheckCircleOutlined className="text-green-500" style={{ fontSize: '64px' }} />
                        <div className="text-2xl font-bold mt-4">Bán phiếu thành công!</div>
                        {printSettings.AllowPrintTicketWhenSell && (
                            <div className="text-sm text-blue-600 mt-2">
                                <PrinterOutlined /> {printSuccess ? 'Đã tự động in vé' : 'Chưa in vé - vui lòng in lại'}
                            </div>
                        )}
                    </div>
                ),
                content: (
                    <div className="space-y-4 mt-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="grid grid-cols-2 gap-3 text-base">
                                <div className="text-gray-600">Sinh viên:</div>
                                <div className="font-semibold text-right">
                                    {selectedStudent?.fullName || selectedStudent?.name}
                                </div>

                                <div className="text-gray-600">MSSV:</div>
                                <div className="font-semibold text-right">
                                    {selectedStudent?.userInternalId || selectedStudent?.studentCode}
                                </div>

                                <div className="text-gray-600">Loại phiếu:</div>
                                <div className="font-semibold text-right">
                                    {selectedTicketType?.name}
                                </div>

                                <div className="text-gray-600">Số lượng:</div>
                                <div className="font-bold text-blue-600 text-right text-lg">
                                    {values.amount} phiếu
                                </div>

                                {/* Chỉ hiện khuyến mãi khi thực sự có phiếu tặng kèm */}
                                {selectedTicketType?.buyQuantity && selectedTicketType?.giftQuantity &&
                                 selectedTicketType.buyQuantity > 0 && selectedTicketType.giftQuantity > 0 &&
                                 Math.floor(values.amount / selectedTicketType.buyQuantity) > 0 && (
                                    <>
                                        <div className="text-gray-600">🎁 Khuyến mãi:</div>
                                        <div className="font-bold text-orange-600 text-right text-lg">
                                            Mua {selectedTicketType.buyQuantity} tặng {selectedTicketType.giftQuantity}
                                        </div>

                                        <div className="text-gray-600">🎁 Phiếu tặng kèm:</div>
                                        <div className="font-bold text-orange-600 text-right text-lg">
                                            {Math.floor(values.amount / selectedTicketType.buyQuantity) * selectedTicketType.giftQuantity} phiếu
                                        </div>

                                        <div className="text-gray-600">📊 Tổng nhận:</div>
                                        <div className="font-bold text-purple-600 text-right text-xl">
                                            {values.amount + Math.floor(values.amount / selectedTicketType.buyQuantity) * selectedTicketType.giftQuantity} phiếu
                                        </div>
                                    </>
                                )}

                                <div className="text-gray-600">Tổng tiền:</div>
                                <div className="font-bold text-green-600 text-right text-xl">
                                    {formatCurrency((selectedTicketType?.price || 0) * values.amount)}
                                </div>

                                {values.isUsed && (
                                    <>
                                        <div className="text-gray-600">Trạng thái:</div>
                                        <div className="font-semibold text-orange-600 text-right">
                                            ✅ Đã tự động sử dụng
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {response?.result && Array.isArray(response.result) && response.result.length > 0 && (
                            <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300">
                                <div className="font-semibold text-green-700 mb-2">Mã phiếu đã tạo:</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {response.result.map((ticket: any, index: number) => (
                                        <div key={index} className="bg-white px-3 py-2 rounded border border-green-200 text-center font-mono font-bold text-blue-600">
                                            {ticket.code}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Nút In lại */}
                        <div className="mt-4">
                            <Button
                                type="primary"
                                size="large"
                                block
                                icon={<PrinterOutlined />}
                                onClick={handleReprintTickets}
                                className="focus:outline-none focus:shadow-none active:shadow-none"
                            >
                                In lại tất cả
                            </Button>
                        </div>
                    </div>
                ),
                width: 650,
                centered: true,
                maskClosable: true,
                okText: 'Đóng',
                okButtonProps: {
                    size: 'large',
                    className: 'focus:outline-none focus:shadow-none active:shadow-none'
                }
            });

            const currentTicketTypeId = form.getFieldValue('ticketTypeId');
            const currentIsUsed = form.getFieldValue('isUsed');
            form.resetFields();
            form.setFieldsValue({
                ticketTypeId: currentTicketTypeId,
                isUsed: currentIsUsed,
                amount: 1
            });
            setCurrentAmount(1);
            setShowNote(false);
            setSelectedStudent(null);
        } catch (error: any) {
            Modal.error({
                title: (
                    <div className="text-xl font-bold">❌ Lỗi bán phiếu</div>
                ),
                content: (
                    <div className="text-base mt-4">
                        {error?.response?.data?.message || 'Có lỗi xảy ra khi tạo phiếu'}
                    </div>
                ),
                centered: true,
                okText: 'Đóng',
                okButtonProps: {
                    size: 'large',
                    className: 'focus:outline-none focus:shadow-none active:shadow-none'
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const setAmount = (amount: number) => {
        form.setFieldsValue({ amount });
        setCurrentAmount(amount);
    };

    const handleAmountChange = (value: number | null) => {
        if (value) {
            setCurrentAmount(value);
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <Card className="shadow-lg">
                <div className="space-y-3 sm:space-y-4">
                    <h2 className="text-lg sm:text-2xl font-bold text-gray-700 flex items-center gap-2">
                        <ShoppingOutlined className="text-green-500" />
                        <span className="hidden xs:inline">Bán phiếu mới</span>
                        <span className="xs:hidden">Bán phiếu</span>
                    </h2>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        size="large"
                        initialValues={{ amount: 1, isUsed: false }}
                        className="ticket-sale-form"
                    >
                        {/* Student Selection with AutoComplete */}
                        <Form.Item
                            className="mb-3"
                            label={<span className="text-base sm:text-lg font-semibold">Sinh viên</span>}
                            name="studentId"
                            rules={[{ required: true, message: 'Vui lòng chọn sinh viên' }]}
                        >
                            {selectedStudent ? (
                                <div className="border-2 border-green-400 rounded-lg p-3 sm:p-4 bg-green-50">
                                    <div className="flex justify-between items-center">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-base sm:text-lg text-green-700 truncate">
                                                {selectedStudent.fullName || selectedStudent.name}
                                            </div>
                                            <div className="text-xs sm:text-sm text-gray-600">
                                                MSSV: {selectedStudent.userInternalId || selectedStudent.studentCode}
                                            </div>
                                        </div>
                                        <Button
                                            type="link"
                                            danger
                                            size="small"
                                            className="focus:outline-none focus:shadow-none active:shadow-none flex-shrink-0"
                                            onClick={() => {
                                                setSelectedStudent(null);
                                                form.setFieldsValue({ studentId: undefined });
                                            }}
                                        >
                                            Đổi
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <AutoComplete
                                    value={studentSearchText}
                                    onChange={handleStudentInputChange}
                                    onSelect={handleStudentSelect}
                                    options={studentSuggestions}
                                    filterOption={false}
                                    notFoundContent={searchingStudent ? 'Đang tìm kiếm...' : 'Không tìm thấy sinh viên'}
                                >
                                    <Input
                                        size="large"
                                        prefix={<SearchOutlined />}
                                        placeholder="Nhập tên hoặc MSSV..."
                                        onKeyPress={handleStudentKeyPress}
                                        autoFocus
                                    />
                                </AutoComplete>
                            )}
                        </Form.Item>

                        {/* Ticket Type Selection */}
                        <Form.Item
                            className="mb-3"
                            label={<span className="text-base sm:text-lg font-semibold">Loại phiếu</span>}
                            name="ticketTypeId"
                            rules={[{ required: true, message: 'Vui lòng chọn loại phiếu' }]}
                        >
                            <Select
                                placeholder="Chọn loại phiếu"
                                onChange={handleTicketTypeChange}
                            >
                                {ticketTypes.map(type => (
                                    <Option key={type.id} value={type.id}>
                                        <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center py-1 sm:py-2">
                                            <div>
                                                <div className="text-sm sm:text-base"><b>{type.name}</b> <span className="text-xs sm:text-sm">(Hạn: {type.defaultExpireDays} ngày)</span></div>
                                            </div>
                                            <div className="text-green-600 font-bold text-sm sm:text-lg">
                                                {formatCurrency(type.price)}
                                            </div>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {selectedTicketType && selectedTicketType.buyQuantity && selectedTicketType.giftQuantity &&
                         selectedTicketType.buyQuantity > 0 && selectedTicketType.giftQuantity > 0 && (
                            <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg border-2 border-orange-400 bg-gradient-to-r from-orange-50 to-yellow-50">
                                <div className="flex items-start gap-2 sm:gap-3">
                                    <div className="text-2xl sm:text-3xl">🎁</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-base sm:text-lg text-orange-700 mb-1 sm:mb-2">
                                            KM: Mua {selectedTicketType.buyQuantity} tặng {selectedTicketType.giftQuantity}
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-700 space-y-1">
                                            <p className="hidden sm:block">• Mua {selectedTicketType.buyQuantity} phiếu sẽ được tặng thêm {selectedTicketType.giftQuantity} phiếu</p>
                                            <p className="font-semibold text-red-600 text-[10px] sm:text-sm">⚠️ Tự động dùng không áp dụng cho phiếu tặng kèm</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Amount Selection */}
                        <Form.Item
                            label={<span className="text-base sm:text-lg font-semibold">Số lượng</span>}
                        >
                            <Form.Item
                                name="amount"
                                noStyle
                                rules={[
                                    { required: true, message: 'Vui lòng nhập số lượng' },
                                    { 
                                        type: 'number', 
                                        min: 1, 
                                        max: 30, 
                                        message: 'Số lượng phải từ 1 đến 30',
                                        transform: (value) => Number(value)
                                    }
                                ]}
                            >
                                <InputNumber
                                    min={1}
                                    max={30}
                                    className="w-full"
                                    size="large"
                                    placeholder="Số lượng (1-30)"
                                    style={{ width: '100%' }}
                                    onChange={handleAmountChange}
                                />
                            </Form.Item>
                            <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                                <Button onClick={() => setAmount(1)} size="small" className="bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100 hover:border-blue-400 font-semibold text-xs sm:text-sm py-3 px-4">1</Button>
                                <Button onClick={() => setAmount(2)} size="small" className="bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100 hover:border-blue-400 font-semibold text-xs sm:text-sm py-3 px-4">2</Button>
                                <Button onClick={() => setAmount(3)} size="small" className="bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100 hover:border-blue-400 font-semibold text-xs sm:text-sm py-3 px-4">3</Button>
                                <Button onClick={() => setAmount(4)} size="small" className="bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100 hover:border-blue-400 font-semibold text-xs sm:text-sm py-3 px-4">4</Button>
                                <Button onClick={() => setAmount(5)} size="small" className="bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100 hover:border-blue-400 font-semibold text-xs sm:text-sm py-3 px-4">5</Button>
                                <Button onClick={() => setAmount(10)} size="small" className="bg-green-50 border-green-300 text-green-600 hover:bg-green-100 hover:border-green-400 font-semibold text-xs sm:text-sm py-3 px-4">10</Button>
                            </div>
                        </Form.Item>

                        {/* Auto Use Toggle and Add Note Button */}
                        <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2 xs:gap-0 mb-3 sm:mb-4">
                            <Form.Item
                                className="mb-0 focus:outline-none focus:shadow-none active:shadow-none"
                                label={<span className="text-base sm:text-lg font-semibold mr-2 sm:mr-3">Tự động dùng</span>}
                                name="isUsed"
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                            >
                                <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
                            </Form.Item>
                            {!showNote && (
                                <Button type="dashed" size="small" onClick={() => setShowNote(true)} className="text-gray-600 focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm">
                                    + Ghi chú
                                </Button>
                            )}
                        </div>

                        {/* Price Display */}
                        {selectedTicketType && (
                            <div className="mb-3 sm:mb-4">
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-700 text-sm sm:text-lg">Giá phiếu:</span>
                                        <span className="font-bold text-lg sm:text-2xl text-blue-600">
                                            {formatCurrency(selectedTicketType.price)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-700 text-sm sm:text-lg font-semibold">Tổng tiền:</span>
                                        <span className="font-bold text-xl sm:text-3xl text-green-600">
                                            {formatCurrency(selectedTicketType.price * currentAmount)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Note */}
                        {showNote && (
                            <Form.Item
                                label={
                                    <div className="flex justify-between items-center w-full">
                                        <span className="text-base sm:text-lg font-semibold">Ghi chú</span>
                                        <Button
                                            type="text"
                                            size="small"
                                            danger
                                            onClick={() => {
                                                setShowNote(false);
                                                form.setFieldsValue({ note: undefined });
                                            }}
                                        >
                                            Ẩn
                                        </Button>
                                    </div>
                                }
                                name="note"
                            >
                                <TextArea rows={2} placeholder="Nhập ghi chú nếu cần..." className="text-sm sm:text-base" />
                            </Form.Item>
                        )}

                        {/* Submit Button */}
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                block
                                loading={loading}
                                icon={<DollarOutlined />}
                                className="h-12 sm:h-16 text-base sm:text-xl font-bold bg-gradient-to-r from-green-500 to-blue-500 border-0
                                focus:outline-none focus:shadow-none active:shadow-none"
                            >
                                Bán phiếu
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </Card>
        </div>
    );
};

export default TicketSale;
